import { COMPANY_IS_INACTIVE } from "@/lib/genesis-error-codes";
import { hasErrorCode } from "@/pages/company/company-admin-error";
// Pure helpers + shared types for SHARE items. Kept separate from the
// component file so Vite's Fast Refresh isn't disabled by mixed exports.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ShareItemLike = any;

/**
 * Whether the caller may edit or delete this item — and, negated, whether they may apply to or rate
 * it.
 *
 * <b>An either/or, never an OR.</b> `owner` answers "did I write this" and is derived from
 * `person_id`, which nothing ever rewrites. For a COMPANY item that makes it useless as a
 * permission: the admin who wrote a post keeps `owner === true` on it after being demoted to member
 * or removed from the company, while `isAdminOfCompany` correctly turns false. `owner ||
 * isAdminOfCompany` would therefore offer that person an Edit button genesis refuses, because for a
 * company item genesis consults membership alone and never looks at who wrote it.
 *
 * So: a personal item asks `owner`, a company item asks `isAdminOfCompany`, and neither borrows from
 * the other. Concept v9, `company-profiles/0002-company-account-model` §6 — "Autorschaft ist bei
 * Firmen-Inhalten KEIN Recht, auch nicht für die Person, die es geschrieben hat".
 *
 * <b>A hint, not a gate.</b> genesis enforces every write against the item's stored `company_id`;
 * this only decides what the UI bothers to offer.
 */
export const mayManageItem = (item: ShareItemLike): boolean =>
  item?.company ? Boolean(item.isAdminOfCompany) : Boolean(item?.owner);

/**
 * The user-facing sentence for a frozen company's content, or null when the failure is something
 * else.
 *
 * <b>Why this exists rather than falling through to the generic message.</b> genesis classifies the
 * refusal as VALIDATION, which `userErrorMessage` has no branch for, so without this every attempt to
 * touch a dormant company's content read "Something went wrong. Please try again." — advice that is
 * both wrong and unactionable, since trying again is exactly what will not help. The concept names
 * the requirement: the code "must NOT be collapsed into the generic authorisation refusal", because
 * the caller who sees it IS an admin and the repair is to reactivate the company.
 */
export const companyFrozenMessage = (
  intl: { formatMessage: (d: { id: string; defaultMessage: string }) => string },
  error: unknown,
): string | null =>
  hasErrorCode(error, COMPANY_IS_INACTIVE)
    ? intl.formatMessage({
        id: "company.inactive.frozen",
        defaultMessage:
          "This company is inactive, so its posts and projects are frozen. Reactivate the company to publish or change anything in its name.",
      })
    : null;

/**
 * The same refusal, for somebody who CANNOT repair it.
 *
 * <b>Why there are two of these.</b> GENESIS001250 reaches two very different callers. An admin of
 * the company meets it on create, edit and delete, and for them "reactivate the company" is the
 * repair — {@link companyFrozenMessage} says so, and must keep saying so. A reader meets the same
 * code on sign-up and rating, and telling them to reactivate a company they have no rights over is
 * an instruction they cannot follow: it reads as the product blaming them for a state somebody else
 * chose. They get the fact and no instruction, which is all there is for them to do with it.
 *
 * <p>The distinction is the CALLER's, not the error's — genesis raises one code, and it is right to,
 * because the cause is one thing. Which sentence to show is a question about who is asking, so it is
 * answered at the call site rather than by inspecting the error further.
 */
export const companyFrozenReaderMessage = (
  intl: { formatMessage: (d: { id: string; defaultMessage: string }) => string },
  error: unknown,
): string | null =>
  hasErrorCode(error, COMPANY_IS_INACTIVE)
    ? intl.formatMessage({
        id: "company.inactive.frozenReader",
        defaultMessage:
          "This company is inactive, so its posts and projects are frozen.",
      })
    : null;

export const itemTypeLabel = (t: string | null | undefined): string => {
  if (t === "PROJECT") return "Projekt";
  if (t === "POST") return "Post";
  return t ?? "";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const creatorName = (c: any): string => {
  if (!c) return "";
  const parts = [c.name?.given, c.name?.family].filter(Boolean) as string[];
  if (parts.length) return parts.join(" ");
  return c.formattedName ?? "";
};

export const fmtCount = (n: number | null | undefined): string => {
  const v = Number(n ?? 0);
  return v >= 1000 ? (v / 1000).toFixed(1).replace(/\.0$/, "") + "k" : String(v);
};

export const fiveStarRounded = (n: number | null | undefined): string =>
  (Math.round((Number(n) || 0) * 2) / 2).toFixed(1);
